import React, { useState, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useSelector, useDispatch } from 'react-redux';
import { FaDoorOpen, FaSearch, FaSpinner, FaTimes, FaLayerGroup } from 'react-icons/fa';
import {
  addRoomFromDirectory,
  fetchAvailableRoomsByBuilding,
  getBuildingTfoidForObject,
  clearAvailableRooms
} from '../../../actions/infoTerminalApi';
import styles from './styles.module.scss';

const RoomSelectModal = ({ isOpen, parentId, onClose }) => {
  const dispatch = useDispatch();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [floorFilter, setFloorFilter] = useState('');

  const { availableRooms, availableRoomsLoading, saving, objects } = useSelector(
    (state) => state.infoTerminal
  );

  // Получаем информацию о родительском этаже и здании
  const parentFloor = useMemo(() => {
    return objects.find((obj) => obj.id === parentId);
  }, [objects, parentId]);

  const parentBuilding = useMemo(() => {
    if (!parentFloor) return null;
    return objects.find((obj) => obj.id === parentFloor.parentId);
  }, [objects, parentFloor]);

  // Загружаем аудитории при открытии модалки
  useEffect(() => {
    if (isOpen && parentId) {
      const tfoid = dispatch(getBuildingTfoidForObject(parentId));
      if (tfoid) {
        dispatch(fetchAvailableRoomsByBuilding(tfoid));
      }
      setSearchTerm('');
      setSelectedRoom(null);
      setFloorFilter('');
    }
    return () => {
      if (!isOpen) {
        dispatch(clearAvailableRooms());
      }
    };
  }, [isOpen, parentId, dispatch]);

  // Закрытие по Escape
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen && onClose) {
        onClose();
      }
    };
    if (isOpen) {
      window.addEventListener('keydown', handleEscape);
    }
    return () => {
      window.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  // Получаем уникальные этажи для фильтра
  const availableFloors = useMemo(() => {
    const floors = [...new Set(availableRooms.map((r) => r.floor).filter(Boolean))];
    return floors.sort((a, b) => a - b);
  }, [availableRooms]);

  // Фильтрация аудиторий
  const filteredRooms = useMemo(() => {
    let result = availableRooms;

    // Фильтр по этажу
    if (floorFilter) {
      result = result.filter((r) => r.floor === parseInt(floorFilter, 10));
    }

    // Фильтр по поиску
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        (r) =>
          r.name?.toLowerCase().includes(term) ||
          r.short_name?.toLowerCase().includes(term) ||
          r.type_title?.toLowerCase().includes(term)
      );
    }

    return result;
  }, [availableRooms, searchTerm, floorFilter]);

  // Группировка по этажам
  const groupedRooms = useMemo(() => {
    if (floorFilter) {
      // Если выбран конкретный этаж, не группируем
      return { [floorFilter]: filteredRooms };
    }

    const groups = {};
    filteredRooms.forEach((room) => {
      const floor = room.floor || 0;
      if (!groups[floor]) {
        groups[floor] = [];
      }
      groups[floor].push(room);
    });

    // Сортируем ключи
    const sortedGroups = {};
    Object.keys(groups)
      .sort((a, b) => parseInt(a, 10) - parseInt(b, 10))
      .forEach((key) => {
        sortedGroups[key] = groups[key];
      });

    return sortedGroups;
  }, [filteredRooms, floorFilter]);

  const handleSelect = async () => {
    if (!selectedRoom || saving) return;
    await dispatch(addRoomFromDirectory(parentId, selectedRoom));
    onClose();
  };

  const handleRowClick = (room) => {
    setSelectedRoom(room);
  };

  const handleRowDoubleClick = (room) => {
    setSelectedRoom(room);
    setTimeout(() => handleSelect(), 0);
  };

  if (!isOpen) return null;

  const buildingName = parentBuilding?.name || 'неизвестное здание';
  const floorName = parentFloor?.name || 'неизвестный этаж';

  return createPortal(
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <span>
            <FaDoorOpen /> Выбор аудитории
          </span>
          <button className={styles.closeBtn} onClick={onClose} title="Закрыть">
            <FaTimes />
          </button>
        </div>

        <div className={styles.contextInfo}>
          Здание: <strong>{buildingName}</strong> | Этаж: <strong>{floorName}</strong>
        </div>

        <div className={styles.filtersContainer}>
          <div className={styles.searchContainer}>
            <FaSearch className={styles.searchIcon} />
            <input
              type="text"
              className={styles.searchInput}
              placeholder="Поиск по названию или типу..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoFocus
            />
          </div>

          {availableFloors.length > 1 && (
            <div className={styles.floorFilter}>
              <FaLayerGroup />
              <select
                value={floorFilter}
                onChange={(e) => setFloorFilter(e.target.value)}
                className={styles.floorSelect}
              >
                <option value="">Все этажи</option>
                {availableFloors.map((floor) => (
                  <option key={floor} value={floor}>
                    {floor} этаж
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        <div className={styles.modalContent}>
          {availableRoomsLoading ? (
            <div className={styles.loadingState}>
              <FaSpinner className={styles.spinner} />
              <span>Загрузка аудиторий...</span>
            </div>
          ) : filteredRooms.length === 0 ? (
            <div className={styles.emptyState}>
              {availableRooms.length === 0
                ? 'Нет доступных аудиторий для этого здания.'
                : 'Ничего не найдено по вашему запросу'}
            </div>
          ) : (
            <div className={styles.roomsList}>
              {Object.entries(groupedRooms).map(([floor, rooms]) => (
                <div key={floor} className={styles.floorGroup}>
                  {!floorFilter && (
                    <div className={styles.floorHeader}>
                      <FaLayerGroup /> {floor === '0' ? 'Без этажа' : `${floor} этаж`}
                      <span className={styles.roomCount}>({rooms.length})</span>
                    </div>
                  )}
                  <table className={styles.roomsTable}>
                    <thead>
                      <tr>
                        <th>Номер</th>
                        <th>Название</th>
                        <th>Тип</th>
                        <th>Вместимость</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rooms.map((room) => (
                        <tr
                          key={room.rid}
                          className={`${styles.roomRow} ${
                            selectedRoom?.rid === room.rid ? styles.selected : ''
                          }`}
                          onClick={() => handleRowClick(room)}
                          onDoubleClick={() => handleRowDoubleClick(room)}
                        >
                          <td className={styles.roomNumber}>{room.short_name || '—'}</td>
                          <td className={styles.roomName}>{room.name}</td>
                          <td className={styles.roomType}>{room.type_title || '—'}</td>
                          <td className={styles.roomVolume}>
                            {room.volume ? `${room.volume} чел.` : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className={styles.modalFooter}>
          <div className={styles.footerInfo}>
            {selectedRoom && (
              <span>
                Выбрано: <strong>{selectedRoom.short_name || selectedRoom.name}</strong>
                {selectedRoom.type_title && ` (${selectedRoom.type_title})`}
              </span>
            )}
          </div>
          <div className={styles.footerActions}>
            <button
              className={`${styles.btn} ${styles.btnSecondary}`}
              onClick={onClose}
              disabled={saving}
            >
              Отмена
            </button>
            <button
              className={`${styles.btn} ${styles.btnPrimary}`}
              onClick={handleSelect}
              disabled={!selectedRoom || saving}
            >
              {saving ? (
                <>
                  <FaSpinner className={styles.spinner} /> Добавление...
                </>
              ) : (
                'Добавить аудиторию'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default RoomSelectModal;
